import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'antd';
import classNames from 'classnames';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import ChatbotModel from '@mm/state/model/ChatbotModel';
import CompanyModel from '@mm/state/model/CompanyModel';
import {
  activeChatbotSelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { Logo } from '../../Logo/Logo';
import styles from './NewSidebarHeader.module.css';
import { UserRoleContainer } from '../../../../common/hooks/use-route-guard';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import { trackWebAppGAEvent } from '../../../../common/helpers/plugins/website-google-analytics';
import { FreeTrialCountdown } from '../../FreeTrialCountdown/FreeTrialCountdown';
import { AllFeaturesDropdown } from '../AllFeaturesDropdown/AllFeaturesDropdown';
import SendLimitAlert from '../SendLimitAlert/SendLimitAlert';
import { PageSwitcher } from '../PageSwitcher/PageSwitcher';
import UserDropdown from '../../UserDropdown/UserDropdown';
import { TemplateGoButton } from '../../../../editor/features/beta/modules/Automations/AutomationHome/TemplateIndex/TemplateGoButton';
import { AutomationTemplateContainer } from '../../../../editor/features/beta/modules/Automations/AutomationHome/TemplateIndex/AutomationTemplateContainer';
import { PlusOutlined } from '@ant-design/icons';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import { PrimaryButton } from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useHistory } from 'react-router-dom';
import { useTokenLogin } from '../../../../modules/Shopify/use-token-login';

import { shopifyRoutes } from '../../../../modules/Shopify/constants';

type Props = {
  children: ReactNode;
  facebookPage: EnhancedBillingPage;
  onMenuClick: () => void;
  setSidebarMinimized: (isMinimized: boolean) => void;
  sidebarMinimized: boolean;
  company: CompanyModel;
  activeChatbot: ChatbotModel | null;
  isFreeTrialActive?: boolean;
};

export const NewSidebarHeader = (props: Props) => {
  const {
    facebookPage,
    onMenuClick,
    company,
    activeChatbot,
    children,
    isFreeTrialActive,
  } = props;
  const isShopify = useSelector(selectIsShopifyContext);
  const login = useTokenLogin();

  const planInfo = getPlanInfo(facebookPage);
  const chatbot = useSelector(activeChatbotSelector);
  const history = useHistory();

  const hideSendLimitAlert =
    planInfo.isCustomTeam ||
    planInfo.isLegacyTeamPlan ||
    (facebookPage.subscription &&
      isBilling2023Subscription(facebookPage.subscription));

  const {
    roleFlags: { isLimitedRole },
  } = UserRoleContainer.useContainer();

  let logoHref = '';

  if (chatbot?.id && !isLimitedRole) {
    logoHref = BotBuilderRoutes.dashboard.getLink({
      chatbotId: chatbot.id,
    });
  }

  const onLogoClick = () => {
    trackWebAppGAEvent('click', {
      category: 'navigation',
      label: 'navbar_logo',
    });
  };

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.wrapperWithTrial]: isFreeTrialActive,
      })}
    >
      <FreeTrialCountdown />
      <div className={styles.header}>
        <div className={styles.leftSide}>
          <div className={styles.logo}>
            <Logo href={logoHref} onClick={onLogoClick} />
          </div>

          {children}
          {!isShopify && <AllFeaturesDropdown />}
        </div>

        {isShopify ? (
          <>
            <Button
              className={styles.shopifyBillingLink}
              onClick={() => history.push(shopifyRoutes.billing.path)}
              type="text"
            >
              Billing
            </Button>
            <div className={styles.shopifyMenu}>
              <Button onClick={login.logout}>Logout</Button>
            </div>
          </>
        ) : (
          <div className={styles.rightSide}>
            {!hideSendLimitAlert && <SendLimitAlert />}
            {!isLimitedRole && (
              <>
                {facebookPage?.getemailsActivated || !activeChatbot?.id ? (
                  <TemplateGoButton
                    label="Automation"
                    leftIcon={
                      <PlusOutlined
                        className={styles.addAutomationButtonIcon}
                      />
                    }
                    css={{ button: styles.addAutomationButton }}
                  />
                ) : (
                  <PrimaryButton
                    className={styles.addAutomationButton}
                    onClick={() =>
                      history.push(
                        BotBuilderRoutes.xRay.getLink({
                          chatbotId: activeChatbot?.id,
                        }),
                      )
                    }
                  >
                    Install X-Ray Pixel
                  </PrimaryButton>
                )}
              </>
            )}
            <PageSwitcher
              facebookPage={facebookPage}
              onMenuClick={onMenuClick}
            />
            <UserDropdown isFreeTrialActive={isFreeTrialActive} />
          </div>
        )}
      </div>
    </div>
  );
};
